<template>
  <div>
    <v-card v-if="$isSectionEnabled('access','credentials')" class="elevation-0">
        <v-card-title class="text-h6 font-weight-bold">
          {{ `${$vuetify.lang.t("$vuetify.project_credentials.credentials_label_txt")}` }}
        </v-card-title>
        <v-divider></v-divider>
        <v-row class="mx-2 mb-3">
          <v-col cols="12" md="6">
            <v-card class="pa-2" flat>
              <strong>{{
                `${$vuetify.lang.t("$vuetify.project_credentials.all_credentials_label_txt")}:`
              }}</strong>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-row class="justify-end mt-1">
              <Button
                :buttonType="FLOATING_ACTION_BUTTON"
                iconName="mdi-plus"
                size="x-small"
                :buttonText="$vuetify.lang.t('$vuetify.common.create')"
                color="primary"
                @onClick="createNewClick()"
                :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
              />
            </v-row>
          </v-col>
        </v-row>
        <div>
          <Loader v-if="isLoading" class="mr-2 mt-6" />
          <v-data-table
            v-else
            :headers="credentialHeaders"
            :items="credentials"
            :sort-by="['created_at']"
            :sort-desc="[true]"
          >
            <template v-slot:item.id="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{
                    item.id.split("-")[0]
                  }}</span>
                </template>
                <span>{{ item.id }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.name="{ item }">
              {{ item.name ? item.name : "N/A" }}
            </template>
            <template v-slot:item.status="{ item }">
              {{ item.status ? capitalizeFirstLetter(item.status) : "N/A" }}
            </template>
            <template v-slot:item.security="{ item }">
              <ChatHeadTooltip
                :toolTipText="
                  getSecurityLevelsDescriptions(item.security?.level)
                "
                :chipText="item.security?.level"
                :chipColor="item.security?.color"
                :textColor="item.security?.textColor"
              />
            </template>
            <template v-slot:item.created_at="{ item }">
              {{ item.created_at ? formatDateTime(item.created_at) : "N/A" }}
            </template>
            <template v-slot:item.actions="{ item }">
              <ButtonGroup
                :groupType="ACTION_BUTTON_GROUP"

                :detailsButton="false"
                :addButton="false"
                :deleteButton="false"

                :startButton="item.status === 'inactive'"
                :stopButton="item.status === 'active'"

                :disabledStartButton="!$hasProjectAccessFor([$pRole.ADMIN]) || selectedProject.status !== 'running'"
                :disabledEditButton="!$hasProjectAccessFor([$pRole.ADMIN]) || selectedProject.status !== 'running'"
                :disabledStopButton="!$hasProjectAccessFor([$pRole.ADMIN]) || selectedProject.status !== 'running'"

                @onEditClick="editItem(item)"
                @onStartClick="startItem(item)"
                @onStopClick="stopItem(item)"
              />
            </template>
          </v-data-table>
          <v-form ref="form" v-model="valid">
            <v-dialog v-model="dialog" width="500">
              <v-card>
                <div class="px-3 py-3 primary--text font-weight-bold">
                  {{
                    edit
                      ? $vuetify.lang.t("$vuetify.project_credentials.edit_credential_label_txt")
                      : $vuetify.lang.t("$vuetify.project_credentials.add_credential_label_txt")
                  }}
                </div>
                <v-divider></v-divider>
                <v-layout class="px-5 py-5">
                  <v-row>
                    <v-col cols="12" md="12">
                      <v-text-field
                        :inputType="TEXT"
                        v-model="credentialName"
                        :label="$vuetify.lang.t('$vuetify.project_credentials.credentials_name_header_txt')"
                        :rules="[
                          v =>
                            !!v ||
                            $vuetify.lang.t('$vuetify.project_credentials.credentials_error_msg_txt'),
                        ]"
                        outlined
                        dense
                      ></v-text-field>

                      <template>
                        <div>
                          <v-select
                            :label="$vuetify.lang.t('$vuetify.project_credentials.key_type_selector_txt')"
                            :items="credentialTypeOptions"
                            :disabled="edit"
                            variant="solo-filled"
                            v-model="credentialType"
                            :key="dialog"
                            outlined
                            dense
                          >
                            <template v-slot:append>
                              <v-tooltip top v-if="!edit">
                                <template v-slot:activator="{ on }">
                                  <v-avatar v-on="on">
                                    <v-icon>mdi-information</v-icon>
                                  </v-avatar>
                                </template>
                                <span v-if="credentialType === 'project'">
                                  {{
                                    $vuetify.lang.t(
                                      "$vuetify.project_credentials.project_api_key_tooltip_txt",
                                    )
                                  }}
                                </span>
                                <span v-else-if="credentialType === 'client'">
                                  {{
                                    $vuetify.lang.t("$vuetify.project_credentials.client_key_tooltip_txt")
                                  }}
                                </span>
                              </v-tooltip>
                              <v-icon>mdi-chevron-down</v-icon>
                            </template>
                          </v-select>
                        </div>
                      </template>

                      <template>
                        <div>
                          <v-combobox
                            v-model="whitelistedDomainShow"
                            :label="$vuetify.lang.t('$vuetify.project_credentials.whitelisted_domain_label_txt')"
                            :placeholder="
                              $vuetify.lang.t(
                                '$vuetify.project_credentials.whitelisted_domain_placeholder_txt'
                              )
                            "
                            multiple
                            small-chips
                            outlined
                            append-icon=""
                            deletable-chips
                            clearable
                            height="90"
                            @keydown.space.prevent
                            :rules="[validateWhitelistedDomain]"
                          >
                          </v-combobox>
                        </div>
                      </template>

                      <v-alert
                        v-if="whitelistedDomainShow.length === 0"
                        outlined
                        dense
                        prominent
                        text
                        border="left"
                        type="warning"
                      >
                      <span
                        v-html="
                          $vuetify.lang.t(
                            '$vuetify.project_credentials.whitelisted_domain_security_alert_txt'
                          )
                        "
                      ></span>
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-layout>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <Button
                    class="red--text font-weight-bold"
                    :buttonType="TEXT_BUTTON"
                    :buttonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
                    color="red"
                    @onClick="dialog = false"
                  />
                  <Button
                    class="font-weight-bold"
                    :buttonType="RAISED_BUTTON"
                    :buttonText="$vuetify.lang.t('$vuetify.common.save_btn')"
                    :disabled="!valid"
                    :color="valid ? 'primary' : '#90A4AE'"
                    @onClick="saveCredentialClick()"
                    :loading="isLoading"
                  />
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-form>
        </div>
    </v-card>

    <v-card  v-if="$isSectionEnabled('access','external_system_encryption')" class="elevation-0 mb-6">
      <v-card-title class="text-h6 font-weight-bold d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          {{ $vuetify.lang.t("$vuetify.project_credentials.external_system.title") }}
          <v-tooltip bottom max-width="350">
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs" class="info-icon">i</span>
            </template>
            <span>{{ $vuetify.lang.t("$vuetify.project_credentials.external_system.external_system_tooltip") }}</span>
          </v-tooltip>
        </div>

      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-row class="align-center">
          <v-col cols="12" md="8">
            <div class="text-subtitle-2 font-weight-medium">
              <div class="d-flex align-center">
                <v-icon color="green" v-if="isExternalKeyAvailable">mdi-check-circle</v-icon>
                <v-icon color="grey" v-else>mdi-alert-circle</v-icon>

                <span class="ml-2" :class="isExternalKeyAvailable ? 'green--text' : 'grey--text'">
                  {{ isExternalKeyAvailable ? 
                    $vuetify.lang.t("$vuetify.project_credentials.external_system.external_key_exists") :
                    $vuetify.lang.t("$vuetify.project_credentials.external_system.external_key_not_created")
                  }}
                </span>
              </div>
            </div>

            <v-btn
              class="text-capitalize mt-4"
              color="primary"
              @click="generateExternalKeyClick"
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || selectedProject.status !== 'running' || isLoadingExternal"
              :loading="isLoadingExternal"
            >
              {{ $vuetify.lang.t('$vuetify.project_credentials.external_system.generate_key_btn') }}
            </v-btn>
          </v-col>

          <v-col cols="12" md="4" class="d-flex justify-end">
            <Loader v-if="isLoadingExternal" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>


    <v-dialog v-model="confirmReplaceDialog" persistent width="500">
      <v-card>
        <v-card-title class="headline">
          {{ $vuetify.lang.t("$vuetify.project_credentials.external_system.replace_confirm_title") }}
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" outlined>
            {{ $vuetify.lang.t("$vuetify.project_credentials.external_system.replace_confirm_alert_txt") }}
          </v-alert>
          <div class="mt-3">
            {{ $vuetify.lang.t("$vuetify.project_credentials.external_system.replace_confirm_question_txt") }}
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="confirmReplaceDialog = false"
            :disabled="confirmLoading"
            class="mr-2"
          >
            {{$vuetify.lang.t('$vuetify.common.cancel_btn')}}
          </v-btn>
          <Button
            class="font-weight-bold"
            :buttonType="RAISED_BUTTON"
            :buttonText="$vuetify.lang.t('$vuetify.project_credentials.external_system.generate_key_btn')"
            color="primary"
            @onClick="proceedGenerateExternalKey()"
            :loading="confirmLoading"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="externalDialog" persistent width="600">
      <v-card>
        <v-card-title class="headline white--text text--primary mb-4">
          {{ $vuetify.lang.t("$vuetify.project_credentials.external_system.encrypted_data_title") }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <v-alert type="info" outlined>
              {{ $vuetify.lang.t("$vuetify.project_credentials.external_key_download_warning_message") }}
            </v-alert>
          </div>

          <v-text-field
            outlined
            readonly
            :value="externalKeyData ? externalKeyData.external_system_key : ''"
            :label="$vuetify.lang.t('$vuetify.project_credentials.external_system.external_key_label_txt')"
          >
            <template v-slot:append>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" @click="copyExternalKey">mdi-content-copy</v-icon>
                </template>
                <span>
                  {{
                    external_key_copy
                      ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                      : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                  }}
                </span>
              </v-tooltip>
            </template>
          </v-text-field>

          <div class="mt-4">
            <v-btn color="primary" small @click="downloadExternalKey" :disabled="!externalKeyData">
              <v-icon left>mdi-download</v-icon>
              {{ $vuetify.lang.t('$vuetify.project_credentials.download_json_msg_txt') }}
            </v-btn>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="externalDialog = false"
          >
            {{$vuetify.lang.t('$vuetify.common.close_btn')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="successDialog" persistent width="600">
      <v-card>
        <v-card-title class="headline white--text text--primary mb-4">
          {{ $vuetify.lang.t("$vuetify.project_credentials.credentials_label_txt") }}
        </v-card-title>
        <v-card-text>
          <div class="mb-8">
            <v-alert type="info" outlined>
              {{ $vuetify.lang.t("$vuetify.project_credentials.credentials_download_json_msg_txt") }}
            </v-alert>
          </div>
          <!-- key copy box starts here-->
          <v-text-field
            v-if="credentialType === 'client'"
            v-model="client_key"
            :label="this.$vuetify.lang.t('$vuetify.project_credentials.client_key_label_txt')"
            outlined
          >
            <template v-slot:append>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" @click="copyClientKey()"
                    >mdi-content-copy</v-icon
                  >
                </template>
                {{
                  client_key_copy
                    ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                    : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                }}
              </v-tooltip>
            </template>
          </v-text-field>

          <div v-else>
            <v-text-field
              v-model="project_key"
              :label="$vuetify.lang.t('$vuetify.project_credentials.project_key_label_txt')"
              outlined
            >
              <template v-slot:append>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="copyProjectKey()">
                      mdi-content-copy
                    </v-icon>
                  </template>
                  {{
                    project_key_copy
                      ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                      : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                  }}
                </v-tooltip>
              </template>
            </v-text-field>
            <v-text-field
              v-model="api_key"
              :label="$vuetify.lang.t('$vuetify.project_credentials.api_key_label_txt')"
              outlined
            >
              <template v-slot:append>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="copyApiKey()">
                      mdi-content-copy
                    </v-icon>
                  </template>
                  {{
                    api_key_copy
                      ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                      : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                  }}
                </v-tooltip>
              </template>
            </v-text-field>
          </div>
          <!-- key copy box ends here -->
          <v-btn color="primary" @click="downloadCredential()">
            <v-icon>mdi-download</v-icon>
            {{ $vuetify.lang.t("$vuetify.project_credentials.download_json_msg_txt") }}
          </v-btn>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <Button
            :buttonType="TEXT_BUTTON"
            :buttonText="$vuetify.lang.t('$vuetify.common.close_btn')"
            color="primary"
            @onClick="successDialog = false"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script>
import jwt_decode from "jwt-decode";
import { mapState, mapActions, mapGetters } from "vuex";
import {
  FLOATING_ACTION_BUTTON,
  RAISED_BUTTON,
  TEXT_BUTTON
} from "@/components/common/button/_buttontypes";
import { ACTION_BUTTON_GROUP } from "@/components/common/buttongroup/_buttongrouptypes";
import {
  capitalizeFirstLetter,
  formatDateTime,
} from "@/helpers/helper";
import Button from "@/components/common/button/Button";
import { TEXT } from "@/components/common/input/_inputTypes";
import Loader from "@/components/common/loader/Loader";
import {
  ADD_ALERT,
  LOAD_ALL_CREDENTIAL,
  SAVE_CREDENTIAL,
  UPDATE_CREDENTIAL,
  ENCRYPT_EXTERNAL_SYSTEM_DATA,
} from "@/store/_actiontypes";
import ButtonGroup from "@/components/common/buttongroup/ButtonGroup";
import ChatHeadTooltip from "@/components/common/tooltip/ChatHeadTooltip.vue";
import store from "@/store";

export default {
  props: {},
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Button,
    Loader,
    ButtonGroup,
    ChatHeadTooltip
  },
  data() {
    return {
      credentialHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.id_header_txt"),
          align: "center",
          sortable: true,
          value: "id",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.credentials_name_header_txt"),
          align: "center",
          sortable: true,
          value: "name",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.project_credentials.key_type_header_txt"),
          align: "center",
          sortable: true,
          value: "key_type",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.status"),
          align: "center",
          sortable: true,
          value: "status",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.project_credentials.label_header_txt"),
          align: "center",
          sortable: true,
          value: "security",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.created_at"),
          align: "center",
          sortable: true,
          value: "created_at",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      formatDateTime,
      FLOATING_ACTION_BUTTON,
      RAISED_BUTTON,
      TEXT_BUTTON,
      ACTION_BUTTON_GROUP,
      TEXT,
      isLoading: false,
      capitalizeFirstLetter,
      valid: true,
      whitelistedDomainShow: [],
      dialog: false,
      edit: false,
      credentialType: "project",
      credentialName: "",
      successDialog: false,
      api_key: "",
      project_key: "",
      client_key: "",
      api_key_copy: false,
      project_key_copy: false,
      client_key_copy: false,

      isLoadingExternal: false,
      externalKeyData: null,
      externalKeyPreview: "",
      external_key_copy: false,
      externalDialog: false,

      // confirm dialog state
      confirmReplaceDialog: false,
      confirmLoading: false,
    }
  },
  created() {
    this.getAllCredential();
  },
  computed: {
    ...mapState({
      selectedProject: state => state.project.selectedProject,
      organization: state => state.account.organization,
      user: (state) => state.account.user, // FIXME we shouldn't store and access user credentials from vuex store. Always use directly from localStorage
    }),
    ...mapGetters("loader", ["loading"]),
    ...mapGetters("project", ["credentials"]),

    isExternalKeyAvailable() {
      if (this.externalKeyData && this.externalKeyData.external_system_key) return true;
      return !!(this.selectedProject && this.selectedProject.has_external_key);
    },
    
    validateWhitelistedDomain() {
      return v => {
        let domains = Array.isArray(this.whitelistedDomainShow)
          ? this.whitelistedDomainShow
          : [];

        if (
          ("project" === this.credentialType ||
            "client" === this.credentialType) &&
          0 === domains.length
        ) {
          return true;
          //return 'Please enter at least one whitelisted domain.'
        }

        // The domain must follow a valid format.
        // For example, "localhost", "example.com", "subdomain.example.com", or "120.0.0.1".
        // Do not include scheme ("https://"), page paths ("example.com/page"), or port numbers ("localhost:8000").
        const domainPattern =
          /^(?!:\/\/)(?=.{1,255}$)(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}|localhost|\b((25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\b)$/;
        const isValid = domains.every(domain => {
          return domainPattern.test(domain);
        });

        this.isValidWhitelistedDomain = isValid;
        if (!isValid) {
          this.valid = false;
        }

        return (
          isValid ||
          this.$vuetify.lang.t("$vuetify.project_credentials.whitelisted_domain_validation_txt")
        );
      };
    },
    credentialTypeOptions() {
      return [
        {
          text: this.$vuetify.lang.t("$vuetify.project_credentials.credential_type.project_api_key_txt"),
          value: "project"
        },
        {
          text: this.$vuetify.lang.t("$vuetify.project_credentials.credential_type.client_key_txt"),
          value: "client"
        }
      ];
    },
    
  },
  watch: {
    whitelistedDomainShow: {
      handler: function () {
        if (this.whitelistedDomainShow != null) {
          this.validateWhitelistedDomain();
        } else {
          console.warn("whitelistedDomainShow is null or undefined");
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("project", [
      LOAD_ALL_CREDENTIAL,
      SAVE_CREDENTIAL,
      UPDATE_CREDENTIAL,
      ENCRYPT_EXTERNAL_SYSTEM_DATA,
    ]),
    ...mapActions("alert", [ADD_ALERT]),
    accountRoles() {
      return jwt_decode(this.user.access_token)["scope"];
    },
    createNewClick() {
      if (this.selectedProject.status === "running") {
        this.resetForm();
        this.edit = false;
        this.credentialName = "";
        this.credentialType = "project";
        this.whitelistedDomain = {};
        this.whitelistedDomainShow = [];
        this.credentialResponse = null;
        this.dialog = true;
      } else if (this.selectedProject.status === "locked") {
        store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: this.alertText.projectLocked, color: "error" },
          { root: true },
        );
      } else {
        store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: this.alertText.createNotAllow, color: "error" },
          { root: true },
        );
      }
    },
    saveCredentialClick() {
      if (this.validateForm()) {
        if (this.isValidWhitelistedDomain) {
          let domains = Array.isArray(this.whitelistedDomainShow)
            ? this.whitelistedDomainShow
            : [];
          this.whitelistedDomain = {
            whitelists: domains,
          };
        }
        if (this.edit) {
          if (this.selectedProject.id && this.toBeUpdatedItem) {
            this.updateCredential({
              project_id: this.selectedProject.id,
              credential_id: this.toBeUpdatedItem,
              name: this.credentialName,
              type: this.credentialType,
              whitelisted: this.whitelistedDomain,
            });
          }
        } else {
          if (this.selectedProject.id) {
            this.saveCredential({
              project_id: this.selectedProject.id,
              name: this.credentialName,
              type: this.credentialType,
              whitelisted: this.whitelistedDomain,
            });
          }
        }
      }
    },
    updateCredential(body_json) {
      this.isLoading = true;
      this.UPDATE_CREDENTIAL(body_json).then(
        response => {
          this.dialog = false;
          this.getAllCredential();
        },
        error => {
          this.isLoading = false;
        },
      );
    },
    stopItem(item) {
      this.updateCredential({
        project_id: this.selectedProject.id,
        credential_id: item.id,
        status: "inactive",
      });
    },
    startItem(item) {
      this.updateCredential({
        project_id: this.selectedProject.id,
        credential_id: item.id,
        status: "active",
      });
    },
    saveCredential(body_json) {
      this.isLoading = true;
      this.SAVE_CREDENTIAL(body_json).then(
        response => {
          this.credentialResponse = response.data.details;
          this.downloadMessage = response.data.message;
          this.api_key = response.data.details.api_key;
          this.project_key = response.data.details.project_key;
          this.client_key = response.data.details.client_key;
          this.dialog = false;
          this.successDialog = true;
          this.getAllCredential();
        },
        error => {
          this.isLoading = false;
        },
      );
    },
    async getAllCredential() {
      this.isLoading = true;
      try {
        await this.LOAD_ALL_CREDENTIAL({
          project_id: this.selectedProject.id,
        });
      } catch (error) {
        console.error("Error loading credentials:", error);
      } finally {
        this.isLoading = false;
      }
    },
    editItem(item) {
      this.edit = true;
      this.toBeUpdatedItem = item.id;
      this.credentialName = item.name;
      this.credentialType = item.key_type;
      this.dialog = true;
      if (item.key_metadata !== null) {
        this.whitelistedDomainShow = item.key_metadata.whitelists;
      }
    },
    copyApiKey() {
      navigator.clipboard.writeText(this.api_key);
      this.api_key_copy = true;
    },
    copyProjectKey() {
      navigator.clipboard.writeText(this.project_key);
      this.project_key_copy = true;
    },
    copyClientKey() {
      navigator.clipboard.writeText(this.client_key);
      this.client_key_copy = true;
    },
    getSecurityLevelsDescriptions(level) {
      switch (level) {
        case "High Risk":
          return this.$vuetify.lang.t(
            "$vuetify.project_credentials.credentials_risk_level_high_risk_description_txt",
          );
        case "Medium":
          return this.$vuetify.lang.t(
            "$vuetify.project_credentials.credentials_risk_level_medium_description_txt",
          );
        default:
          return this.$vuetify.lang.t(
            "$vuetify.project_credentials.credentials_risk_level_no_risk_description_txt",
          );
      }
    },
    resetForm() {
      this.$refs.form.reset();
    },
    validateForm() {
      return this.$refs.form.validate();
    },
    downloadCredential() {
      let json_object = this.credentialResponse;
      let file_name = `${
        this.credentialName.indexOf(" ") >= 0
          ? this.credentialName.trim().replace(/\s/g, "-")
          : this.credentialName
      }-${this.$route.query.pid}.json`;
      if (!window.navigator.msSaveOrOpenBlob) {
        // BLOB NAVIGATOR
        const url = window.URL.createObjectURL(
          new Blob([JSON.stringify(json_object)]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file_name);
        document.body.appendChild(link);
        link.click();
      } else {
        // BLOB FOR EXPLORER 11
        const url = window.navigator.msSaveOrOpenBlob(
          new Blob([JSON.stringify(json_object)]),
          file_name,
        );
      }
    },

    async generateExternalKeyClick() {
      if (!this.$route.query.pid) {
        store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: this.$vuetify.lang.t("$vuetify.project_credentials.external_system.no_project_selected_txt"), color: 'error' },
          { root: true },
        );
        return;
      }

      if (this.isExternalKeyAvailable) {
        this.confirmReplaceDialog = true;
        return;
      }

      await this.proceedGenerateExternalKey();
    },

    async proceedGenerateExternalKey() {
      this.confirmLoading = true;
      this.confirmReplaceDialog = false;

      this.isLoadingExternal = true;
      this.externalKeyData = null;
      this.externalKeyPreview = "";
      this.external_key_copy = false;

      try {
        const resp = await this.ENCRYPT_EXTERNAL_SYSTEM_DATA({
          project_id: this.$route.query.pid,
          body: {},
        });

        const data = resp?.data ?? resp;
        this.externalKeyData = data?.details ?? data;

        try {
          this.externalKeyPreview = JSON.stringify(this.externalKeyData, null, 2);
        } catch (e) {
          this.externalKeyPreview = String(this.externalKeyData);
        }

        this.externalDialog = true;

      } catch (_err) {
        store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: this.$vuetify.lang.t("$vuetify.project_credentials.external_system.generate_failed_txt"), color: 'error' },
          { root: true },
        );
      } finally {
        this.isLoadingExternal = false;
        this.confirmLoading = false;
      }
    },

    copyExternalKey() {
      const key = this.externalKeyData ? this.externalKeyData.external_system_key : this.externalKeyPreview;
      if (!key) return;
      try {
        navigator.clipboard.writeText(key);
        this.external_key_copy = true;
      } catch {
        // ignore
      }
    },

    downloadExternalKey() {
      if (!this.externalKeyData) return;
      const json_object = {
        external_system_key : this.externalKeyData.external_system_key
      };
      const file_name = `external-key-${this.$route.query.pid}.json`;
      if (!window.navigator.msSaveOrOpenBlob) {
        const url = window.URL.createObjectURL(
          new Blob([JSON.stringify(json_object, null, 2)]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file_name);
        document.body.appendChild(link);
        link.click();
      } else {
        window.navigator.msSaveOrOpenBlob(
          new Blob([JSON.stringify(json_object, null, 2)]),
          file_name,
        );
      }
    },

  }
}
</script>


<style scoped>
.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}
</style>